// Path in repo: src/pages/organization-dashboard/index.jsx
//
// Same page, same auth/loading flow as before. Only two things changed:
// 1. TAB_LABELS now covers every key in the new sidebar (including the
//    namespaced sub-items like "team-billing").
// 2. The tab-routing block gains an "analytics" case (renders the new
//    AnalyticsPanel) and routes "team-workspace" to the existing TeamPanel
//    so the team invite/remove flow keeps working unchanged. Every other
//    new key without a built page still falls through to ComingSoonPanel,
//    so nothing is missing or broken.

import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Helmet } from "react-helmet"
import NavigationBar from "../../components/ui/NavigationBar"
import EmailVerificationGate from "../../components/EmailVerificationGate"
import DashboardSidebar from "./components/DashboardSidebar"
import DashboardOverview from "./components/DashboardOverview"
import AnalyticsPanel from "./components/AnalyticsPanel"
import OnChainCredentialsSection from "./components/OnChainCredentialsSection"
import ComingSoonPanel from "./components/ComingSoonPanel"
import LearnersPanel from "./components/LearnersPanel"
import LearningPathsPanel from "./components/LearningPathsPanel"
import AssessmentsPanel from "./components/AssessmentsPanel"
import TeamPanel from "./components/TeamPanel"
import CoursesPanel from "./components/CoursesPanel"
import ProgramsPanel from "./components/ProgramsPanel"
import CohortsPanel from "./components/CohortsPanel"
import SegmentsPanel from "./components/SegmentsPanel"
import InvitationsPanel from "./components/InvitationsPanel"
import MentorsPanel from "./components/MentorsPanel"
import EngagementActivityPanel from "./components/EngagementActivityPanel"
import AnnouncementsPanel from "./components/AnnouncementsPanel"
import CredentialTemplatesPanel from "./components/CredentialTemplatesPanel"
import ProofOfLearningPanel from "./components/ProofOfLearningPanel"
import VerificationPanel from "./components/VerificationPanel"
import IntelligenceLearnersPanel from "./components/IntelligenceLearnersPanel"
import IntelligenceCoursesPanel from "./components/IntelligenceCoursesPanel"
import IntelligenceProgramsPanel from "./components/IntelligenceProgramsPanel"
import IntelligenceCommercePanel from "./components/IntelligenceCommercePanel"
import IntelligenceOutcomesPanel from "./components/IntelligenceOutcomesPanel"
import WebhooksPanel from "./components/WebhooksPanel"
import ApiUsagePanel from "./components/ApiUsagePanel"
import SettingsPanel from "./components/SettingsPanel"
import CertificatesPanel from "./components/CertificatesPanel"
import CommerceRevenuePanel from "./components/CommerceRevenuePanel"
import CommerceProductsPanel from "./components/CommerceProductsPanel"
import CommerceCustomersPanel from "./components/CommerceCustomersPanel"
import CommerceOrdersPanel from "./components/CommerceOrdersPanel"
import { authHelpers } from "../../firebaseClient"
import { SidebarProvider, SidebarInset, SidebarTrigger } from "../../components/ui/sidebar"
import { getUserOrgId, getOrganization } from "../../utils/orgHelpers"

const TAB_LABELS = {
  overview: "Dashboard",

  courses: "Courses",
  "learning-programs": "Programs",
  "learning-paths": "Learning Paths",
  assessments: "Assessments",

  learners: "Learners",
  "people-cohorts": "Cohorts",
  "people-mentors": "Mentors",
  "people-segments": "Segments",
  "people-invitations": "Invitations",

  "commerce-products": "Products",
  "commerce-customers": "Customers",
  "commerce-orders": "Orders",
  "commerce-revenue": "Revenue",

  certificates: "Credentials",
  "credentials-proof": "Proof of Learning",
  "credentials-verification": "Verification",
  "credentials-templates": "Credential Templates",
  "credentials-onchain": "On-chain Credentials",

  analytics: "Overview",
  "intelligence-learners": "Learners",
  "intelligence-courses": "Courses",
  "intelligence-programs": "Programs",
  "intelligence-commerce": "Commerce",
  "intelligence-outcomes": "Outcomes",

  "engagement-activity": "Activity",
  "engagement-announcements": "Announcements",
  "engagement-communications": "Communications",

  "developer-api-keys": "API Keys",
  "developer-webhooks": "Webhooks",
  "developer-api-usage": "API Usage",
  "developer-docs": "Developer Docs",

  "integrations-connected": "Connected Apps",
  "integrations-marketplace": "Integration Marketplace",

  "team-workspace": "Team Members",
  "org-roles": "Roles & Permissions",
  "org-audit-log": "Audit Log",

  "billing-subscription": "Subscription",
  "billing-usage": "Usage",
  "billing-invoices": "Invoices",
  "billing-payment-methods": "Payment Methods",

  settings: "Organization",
  "settings-branding": "Branding",
  "settings-learning": "Learning",
  "settings-credentials": "Credentials",
  "settings-notifications": "Notifications",
  "settings-security": "Security",
  "settings-privacy": "Privacy",
}

const OrganizationDashboard = () => {
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [organization, setOrganization] = useState(null)
  const [activeTab, setActiveTab] = useState("overview")

  useEffect(() => {
    const loadDashboard = async () => {
      const { session, error: sessionError } = await authHelpers.getSession()

      if (sessionError || !session) {
        navigate("/authentication-portal", { replace: true })
        return
      }

      const { orgId, error: orgIdError } = await getUserOrgId(session.user.uid)

      if (orgIdError || !orgId) {
        navigate("/user-dashboard", { replace: true })
        return
      }

      const { organization: orgData } = await getOrganization(orgId)
      setOrganization({ ...orgData, orgId })
      setLoading(false)
    }

    loadDashboard()
  }, [navigate])

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading dashboard...</span>
        </div>
      </div>
    )
  }

  const renderActiveTab = () => {
    switch (activeTab) {
            case "overview":
        return <DashboardOverview organization={organization} onNavigateTab={setActiveTab} />
            case "analytics":
        return <AnalyticsPanel organization={organization} />
      case "credentials-templates":
        return (
          <CredentialTemplatesPanel
            organization={organization}
            currentUserUid={organization?.ownerUid}
          />
        )
      case "credentials-proof":
        return <ProofOfLearningPanel organization={organization} />
      case "credentials-verification":
        return <VerificationPanel organization={organization} />
      case "credentials-onchain":
        return (
          <OnChainCredentialsSection
            organization={organization}
            onNavigateTab={setActiveTab}
            variant="full"
          />
        )
      case "intelligence-learners":
        return <IntelligenceLearnersPanel organization={organization} />
      case "intelligence-courses":
        return <IntelligenceCoursesPanel organization={organization} />
      case "intelligence-programs":
        return <IntelligenceProgramsPanel organization={organization} />
      case "intelligence-commerce":
        return <IntelligenceCommercePanel organization={organization} />
      case "intelligence-outcomes":
        return <IntelligenceOutcomesPanel organization={organization} />
            case "team":
      case "team-workspace":
        return <TeamPanel organization={organization} />
      case "learners":
        return <LearnersPanel organization={organization} />
            case "courses":
        return <CoursesPanel organization={organization} currentUserUid={organization?.ownerUid} />
            case "learning-programs":
        return <ProgramsPanel organization={organization} />
            case "learning-paths":
        return <LearningPathsPanel organization={organization} />
      case "assessments":
        return <AssessmentsPanel organization={organization} />
                        case "people-cohorts":
        return <CohortsPanel organization={organization} />
            case "people-segments":
        return <SegmentsPanel organization={organization} />
            case "people-mentors":
        return <MentorsPanel organization={organization} />
      case "people-invitations":
        return <InvitationsPanel organization={organization} />
            case "engagement-activity":
        return <EngagementActivityPanel organization={organization} onNavigateTab={setActiveTab} />
      case "engagement-announcements":
        return <AnnouncementsPanel organization={organization} />
            case "developer-webhooks":
        return <WebhooksPanel organization={organization} />
      case "developer-api-usage":
        return <ApiUsagePanel organization={organization} />
      case "settings":
        return <SettingsPanel organization={organization} onSaved={setOrganization} />
            case "certificates":
        return <CertificatesPanel organization={organization} />
      case "commerce-revenue":
        return <CommerceRevenuePanel organization={organization} />
      case "commerce-products":
        return <CommerceProductsPanel organization={organization} />
      case "commerce-customers":
        return <CommerceCustomersPanel organization={organization} />
            case "commerce-orders":
        return <CommerceOrdersPanel organization={organization} />
      default:
        return <ComingSoonPanel label={TAB_LABELS?.[activeTab] || "This section"} />
    }
  }

  return (
    <>
      <Helmet>
        <title>{organization?.name || "Organization"} Dashboard - Nesolearn</title>
      </Helmet>

                  <EmailVerificationGate>
      <div id="dashboard-theme-root" className="min-h-screen bg-muted dark:bg-background overflow-x-hidden">
        <NavigationBar themed />

          <SidebarProvider className="items-start pt-24 sm:pt-28">
          <DashboardSidebar
            activeTab={activeTab}
            onTabChange={setActiveTab}
            organization={organization}
          />

          <SidebarInset className="mx-auto w-full max-w-6xl p-4 md:p-6 bg-transparent min-w-0">
            <div className="flex md:hidden mb-4">
              <SidebarTrigger className="border border-border bg-card shadow-sm rounded-lg size-9" />
            </div>
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-foreground">
                {activeTab === "overview" ? "Dashboard" : TAB_LABELS?.[activeTab]}
              </h1>
              {activeTab === "overview" && (
                <p className="text-muted-foreground mt-1">
                  A snapshot of {organization?.name || "your organization"}'s activity on Nesolearn.
                </p>
              )}
            </div>

            {renderActiveTab()}
          </SidebarInset>
        </SidebarProvider>
      </div>
      </EmailVerificationGate>
    </>
  )
}

export default OrganizationDashboard